import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchDevicesData, startDeviceRegistration } from '../services/dataApi';
import { fetchPageData } from '../services/corezoidApi';
import PageLoader from '../components/PageLoader';
import DevicesTable from '../components/DevicesTable';
import AddDeviceModal from '../components/AddDeviceModal';
import SearchBar from '../components/SearchBar';
import { searchInData } from '../utils/searchUtils';
import useUrlPagination from '../hooks/useUrlPagination';
import useDebouncedValue from '../hooks/useDebouncedValue';
import './Page.css';

const Devices = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchDevicesData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    // Extract data array
    let devices = [];
    if (data && data.data && Array.isArray(data.data)) {
      devices = data.data;
    } else if (Array.isArray(data)) {
      devices = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        devices = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        devices = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(devices, searchTerm);
    
    // Preserve data structure
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const devicesCount = pagination?.total ?? (filteredData?.data?.length || 0);

  const generateUniqueRef = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const digits = '0123456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Ensure at least one digit is present
    if (!/\d/.test(result)) {
      const randomPosition = Math.floor(Math.random() * 9);
      result = result.substring(0, randomPosition) + 
               digits.charAt(Math.floor(Math.random() * digits.length)) + 
               result.substring(randomPosition + 1);
    }
    return result;
  };

  const handleAddDevice = async (deviceData) => {
    try {
      // Generate a unique 9-character alphanumeric ref
      const uniqueRef = generateUniqueRef();

      const result = await startDeviceRegistration({
        deviceId: uniqueRef,
        userDeviceName: deviceData.deviceName
      });

      if (result.success) {
        return { success: true, deviceId: uniqueRef, expiresAt: result.expires_at };
      }
      return { success: false, error: result.error || 'Failed to start registration' };
    } catch (err) {
      console.error('Error starting device registration:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const buildChangedFields = (originalData, inputData, fieldMapping) => {
    const changed = {};

    Object.keys(fieldMapping).forEach(formField => {
      const possibleKeys = fieldMapping[formField];
      const newValue = inputData[formField];

      if (newValue === undefined || newValue === '') {
        return;
      }

      let keyFound = false;
      for (const key of possibleKeys) {
        if (key in originalData) {
          keyFound = true;
          if (originalData[key] !== newValue) {
            changed[key] = newValue;
          }
          break;
        }
      }

      if (!keyFound) {
        changed[formField] = newValue;
      }
    });

    return changed;
  };

  const handleEditDevice = async (deviceData, originalDevice) => {
    try {
      // Get device ref for update
      const deviceRef = originalDevice?.ref;

      if (!deviceRef) {
        console.error('Device ref not found:', originalDevice);
        return { success: false, error: 'Device ref not found' };
      }

      // Get all original device data (now on top level)
      const originalData = originalDevice;

      // Map form fields to possible original keys
      const fieldMapping = {
        userDeviceName: ['userDeviceName', 'UserDeviceName', 'user_device_name'],
        status: ['status', 'Status', 'STATUS']
      };

      const changedData = buildChangedFields(originalData, deviceData, fieldMapping);
      if (Object.keys(changedData).length === 0) {
        return { success: true, skipped: true };
      }

      // Formulate request to update device via regular API 2.0
      const ops = [{
        type: "modify",
        obj: "task",
        data: changedData,
        ref: deviceRef
      }];

      const result = await fetchPageData('devices', ops, 30, false);

      if (result.success && result.data) {
        // Check response format
        const responseData = result.data;

        // Check request_proc
        if (responseData.request_proc === 'ok' && responseData.ops && responseData.ops.length > 0) {
          const firstOp = responseData.ops[0];

          if (firstOp.proc === 'ok') {
            loadData();
            return { success: true };
          } else if (firstOp.proc === 'error') {
            const errorMsg = firstOp.description || 'Unknown error';
            return { success: false, error: errorMsg };
          }
        }

        // If response format is unexpected
        return { success: false, error: 'Unexpected response format' };
      } else {
        return { success: false, error: result.error || 'Failed to update device' };
      }
    } catch (err) {
      console.error('Error updating device:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Devices</h1>
          <p>Track registered hardware, registration flow, and current operating status from one place.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Fleet</div>
            <div className="overview-value">{devicesCount}</div>
            <div className="overview-meta">Visible devices</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Registration</div>
            <div className="overview-value">2-step</div>
            <div className="overview-meta">Secure pairing flow enabled</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>Device Registry</h2>
                  <p>Review devices, open details on click, or start a new registration flow.</p>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">50 devices per page</div>
                  <div className="page-chip">{searchTerm ? 'Filtered view' : 'Active list'}</div>
                </div>
              </div>
              <SearchBar 
                onSearch={handleSearch}
                value={searchTerm}
                resultCount={pagination?.total || 0}
                isUpdating={searchTerm !== debouncedSearchTerm}
                placeholder="Search devices..."
              />
              <DevicesTable 
                data={filteredData} 
                onAdd={handleAddDevice}
                onEdit={handleEditDevice}
                onAddClick={() => setIsAddModalOpen(true)}
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
        <AddDeviceModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddDevice}
          onRegistrationComplete={() => loadData(currentPage)}
        />
      </div>
    </div>
  );
};

export default Devices;
